import type { Romanization, RomanizationScheme, RomanizationSyllable, PinyinStyle, Tone } from './types'

/**
 * Readings are stored per syllable at build time, so every display style is a pure
 * transformation here - no conversion library and no runtime cost.
 *
 * The Mandarin tone-marking rules below are specific to pinyin. Tai-lo marks tone with the
 * same family of diacritics but places them by different rules, so it is spelled out at
 * build time and only the display styles are applied here.
 */
/**
 * Superscript form of the tone number, so this style says the same thing as the numbers style.
 * Tone 0 is the neutral tone, which pinyin writes as 5. Tones 5, 7 and 8 are Tai-lo's and pinyin
 * has no equivalent, but dropping them would leave a Tai-lo syllable rendered here looking
 * toneless, which is a different word.
 */
const SUPERSCRIPT: Record<number, string> = {
    0: '⁵',
    1: '¹',
    2: '²',
    3: '³',
    4: '⁴',
    5: '⁵',
    7: '⁷',
    8: '⁸',
}

/** Toneless vowel followed by its four toned forms. Index is the tone. */
const TONE_MARKS: Record<string, readonly string[]> = {
    a: ['a', 'ā', 'á', 'ǎ', 'à'],
    o: ['o', 'ō', 'ó', 'ǒ', 'ò'],
    e: ['e', 'ē', 'é', 'ě', 'è'],
    i: ['i', 'ī', 'í', 'ǐ', 'ì'],
    u: ['u', 'ū', 'ú', 'ǔ', 'ù'],
    'ü': ['ü', 'ǖ', 'ǘ', 'ǚ', 'ǜ'],
}

const VOWELS = new Set(['a', 'o', 'e', 'i', 'u', 'ü'])

/** Some sources write `ü` as `v`; treat them as the same vowel. */
function normaliseVowel(letter: string): string {
    return letter === 'v' ? 'ü' : letter
}

/**
 * Which vowel carries the tone mark: `a` beats `o`, which beats `e`; otherwise
 * the last vowel takes it, which is what makes `niu` → `niú` and `gui` → `guì`.
 */
function toneVowelIndex(letters: readonly string[]): number {
    for (const preferred of ['a', 'o', 'e']) {
        const index = letters.findIndex((letter) => normaliseVowel(letter) === preferred)
        if (index >= 0) return index
    }

    for (let index = letters.length - 1; index >= 0; index--) {
        if (VOWELS.has(normaliseVowel(letters[index]))) return index
    }

    return -1
}

/**
 * Add a tone to a toneless syllable: `hao` + 3 becomes `hǎo`.
 *
 * Pinyin only. `TONE_MARKS` covers tones 0-4, and a tone it does not know - Tai-lo's 7 and 8 -
 * falls back to the unmarked vowel rather than inventing a placement. `base` is expected in
 * lowercase, because the mark tables are: an uppercase input comes back lowercased.
 */
export function applyToneMark(base: string, tone: Tone): string {
    const letters = [...base.toLowerCase()]
    const index = toneVowelIndex(letters)
    if (index < 0) return base

    const vowel = normaliseVowel(letters[index])
    const marks = TONE_MARKS[vowel]
    if (!marks) return base

    letters[index] = marks[tone] ?? vowel
    return letters.join('')
}

/**
 * Compose a reading from syllables, e.g. after changing a tone.
 *
 * Joins with a space, so it is pinyin only: Tai-lo and POJ hyphenate the syllables *within* a
 * word, and a reading may hold several words, so the separators cannot be re-derived from the
 * scheme alone. A caller holding a Tai-lo reading must keep its original written form instead.
 */
export function romanizationFromSyllables(
    syllables: RomanizationSyllable[],
    scheme: RomanizationScheme,
): Romanization {
    return {
        scheme,
        marked: syllables.map((syllable) => syllable.marked).join(' '),
        numbered: syllables
            .map((syllable) =>
                syllable.han ? `${syllable.base}${syllable.tone === 0 ? 5 : syllable.tone}` : syllable.base,
            )
            .join(' '),
        syllables,
    }
}

export interface RenderedSyllable {
    /** Syllable text. For superscript style this excludes the tone marker. */
    text: string
    tone: Tone
    /** True when the tone marker must be appended by the caller as a superscript. */
    superscript: boolean
}

/**
 * What comes before each syllable in the written form, so a renderer can put it back.
 *
 * A reading is stored both as a syllable list and as a string, and the string carries the
 * spelling convention of its language: pinyin separates syllables with a space, while
 * Tai-lo and POJ join the syllables of a word with hyphens. Rendering the list with a
 * hard-coded space would quietly turn `sian-senn` into two words, which is a different
 * thing to a reader of either scheme.
 *
 * Recovered from the written form rather than stored per syllable, so it stays correct for
 * data already built and cannot drift from the text it describes.
 */
/**
 * The separator *inside* a word: pinyin puts a space between syllables, where Tai-lo and POJ
 * hyphenate. Used only when the written form cannot be taken apart.
 */
function separatorFor(scheme: RomanizationScheme): string {
    return scheme === 'pinyin' ? ' ' : '-'
}

export function separatorsOf(romanization: Romanization): string[] {
    const separators: string[] = []
    let cursor = 0

    for (const syllable of romanization.syllables) {
        const at = romanization.marked.indexOf(syllable.marked, cursor)
        if (at < 0) {
            // The written form does not contain this syllable verbatim, which should not happen.
            // Once one syllable cannot be located, nothing after it can be either: `cursor`
            // cannot be advanced past a span that was never found, so the next slice would
            // re-admit this syllable's text and glue it to the following one - the collapse this
            // branch exists to prevent. Give up on recovering the rest and fall back to the
            // scheme's own separator, which at least keeps every syllable a separate token.
            return romanization.syllables.map((_, index) =>
                index === 0 ? '' : separatorFor(romanization.scheme),
            )
        }
        separators.push(romanization.marked.slice(cursor, at))
        cursor = at + syllable.marked.length
    }

    return separators
}

export { isHan } from './han'

/** Unicode superscript for a tone; the neutral tone is written 5, as it is in the numbers style. */
export function toneSuperscript(tone: Tone): string {
    return SUPERSCRIPT[tone] ?? ''
}

export function renderPinyinSyllables(
    romanization: Romanization,
    style: PinyinStyle,
): RenderedSyllable[] {
    return romanization.syllables.map((syllable) => {
        // Tokens that did not come from a Chinese character (ellipses, punctuation,
        // Latin) are passed through untouched and never gain a tone marker.
        if (!syllable.han) {
            return { text: syllable.base, tone: 0 as Tone, superscript: false }
        }

        switch (style) {
            case 'diacritic':
                return { text: syllable.marked, tone: syllable.tone, superscript: false }
            case 'numbers':
                return {
                    text: `${syllable.base}${syllable.tone === 0 ? 5 : syllable.tone}`,
                    tone: syllable.tone,
                    superscript: false,
                }
            case 'superscript':
                return { text: syllable.base, tone: syllable.tone, superscript: true }
        }
    })
}

/** Flat text form, e.g. for `aria-label`, TTS and the review list. */
export function renderPinyinText(romanization: Romanization, style: PinyinStyle): string {
    const separators = separatorsOf(romanization)

    return renderPinyinSyllables(romanization, style)
        .map((syllable, index) => {
            const text = syllable.superscript
                ? `${syllable.text}${toneSuperscript(syllable.tone)}`
                : syllable.text
            return `${separators[index] ?? ' '}${text}`
        })
        .join('')
}
